import type { AttemptResult, Catalogue, Level, SongState } from '@astrasonora/annotation-schema'
import { nightMet, skyCharted, skyComplete } from './progression.ts'
import { statKey, zeroCounts, type PlayerState } from './state.ts'

export interface AttemptOutcome {
  newBest: boolean
  charted: boolean
  /** The night just opened, if the attempt met the current one in both observatories. */
  levelUp: Level | null
  /** The attempt met the last night in both observatories: the sky is charted (visual §6). */
  skyCharted: boolean
  /** The attempt charted the last constellation of the sky: dawn, and the sunrise (visual §6). */
  sunrise: boolean
}

/**
 * An attempt applied to the player state (spec §10): song state (the cleared spans, and charted
 * once every span is), both statistics, last offered, the cleared count and the level-up in one
 * step. The only place these rules run.
 */
export function finishAttempt(
  state: PlayerState,
  attempt: AttemptResult,
  cat: Catalogue,
): { state: PlayerState; outcome: AttemptOutcome } {
  const prev = state.songs[attempt.songId]
  // spans stay cleared (spec §7): the row's keys the song still has, plus this run's
  const current = new Set(attempt.spans)
  const kept = (prev?.clearedSpans ?? []).filter((k) => current.has(k))
  const clearedSpans = [...kept, ...attempt.clearedSpans.filter((k) => !kept.includes(k))]
  const chartedNow =
    attempt.spans.length > 0 && attempt.spans.every((k) => clearedSpans.includes(k))
  const wasCharted = prev?.status === 'charted'
  const newBest = attempt.clearedSpans.some((k) => !kept.includes(k))

  const song: SongState = {
    songId: attempt.songId,
    status: chartedNow || wasCharted ? 'charted' : 'unfinished',
    clearedSpans,
    chartedObservatory: wasCharted
      ? (prev?.chartedObservatory ?? attempt.observatory)
      : chartedNow
        ? attempt.observatory
        : null,
    chartedLevel: wasCharted
      ? (prev?.chartedLevel ?? attempt.level)
      : chartedNow
        ? attempt.level
        : null,
  }

  const functionStats = { ...state.functionStats }
  const transitionStats = { ...state.transitionStats }
  for (const a of attempt.answers) {
    const fk = statKey(attempt.observatory, a.cell)
    const f = { ...(functionStats[fk] ?? zeroCounts()) }
    bump(f, a.outcome)
    functionStats[fk] = f
    if (a.prevCell !== null) {
      const tk = statKey(attempt.observatory, `${a.prevCell}>${a.cell}`)
      const t = { ...(transitionStats[tk] ?? zeroCounts()) }
      bump(t, a.outcome)
      transitionStats[tk] = t
    }
  }

  let unlocked = state.night.unlocked
  let levelUp: Level | null = null
  const nextState: PlayerState = {
    ...state,
    songs: { ...state.songs, [attempt.songId]: song },
    functionStats,
    transitionStats,
  }
  // the night ends when both observatories have met it (spec §8)
  if (chartedNow && !wasCharted && attempt.level === unlocked && unlocked < 7) {
    if (nightMet(nextState, cat, attempt.level)) {
      unlocked = (unlocked + 1) as Level
      levelUp = unlocked
    }
  }
  const offered = { ...state.night.lastOffered }
  offered[attempt.observatory] = {
    ...(offered[attempt.observatory] ?? {}),
    [String(attempt.level)]: attempt.songId,
  }
  nextState.night = { unlocked, lastOffered: offered }
  const charting = chartedNow && !wasCharted
  return {
    state: nextState,
    outcome: {
      newBest,
      charted: chartedNow,
      levelUp,
      skyCharted: charting && skyCharted(nextState, cat) && !skyCharted(state, cat),
      sunrise: charting && skyComplete(nextState, cat) && !skyComplete(state, cat),
    },
  }
}

function bump(
  c: { correctLive: number; correctPause: number; wrong: number },
  o: 'live' | 'pause' | 'wrong',
) {
  if (o === 'live') c.correctLive++
  else if (o === 'pause') c.correctPause++
  else c.wrong++
}

/** The song's spans the player has cleared, in song order (spec §7): keys the state holds that the song still has. */
export function clearedSpanKeys(row: SongState | undefined, keys: readonly string[]): string[] {
  if (!row) return []
  const have = new Set(row.clearedSpans)
  return keys.filter((k) => have.has(k))
}
